import { Plane, TowerControl, IdCardLanyard, Users, ArrowRight } from '@/components/icons'
import dayjs from 'dayjs'
import { TripStatusBadge } from './TripStatusBadge'
import { toStatusBadge, type TripState } from '@/lib/tripState'

interface TripCardProps {
  tripName: string
  /** Unique registrations across the trip's flights (aircraft is per-flight) */
  aircraftRegistrations?: string[]
  startDate: string | Date | null
  endDate: string | Date | null
  tripState: TripState
  flightsCount: number
  airportsCount: number
  crewCount: number
  paxCount: number
  /**
   * 'list' (default): trip name + pill row, registrations + dates row.
   * 'detail': single row of date range + status pill — the trip name lives in
   * the page header and aircraft is shown per-flight.
   */
  variant?: 'list' | 'detail'
  /** Replaces the static status pill — the detail view passes <TripStatusSelect />. */
  statusBadge?: React.ReactNode
}

export function TripCard({
  tripName,
  aircraftRegistrations = [],
  startDate,
  endDate,
  tripState,
  flightsCount,
  airportsCount,
  crewCount,
  paxCount,
  variant = 'list',
  statusBadge,
}: TripCardProps) {
  const status = toStatusBadge(tripState)
  const badge = statusBadge ?? <TripStatusBadge status={status} />

  const bgClass =
    status === 'complete' ? 'completed-trip-card-bg'
    : status === 'draft' ? 'draft-trip-card-bg'
    : status === 'scheduled' ? 'scheduled-trip-card-bg scheduled-trip-card-shadow'
    : 'trip-card-bg'

  return (
    <div
      className={`${bgClass} flex flex-col overflow-hidden rounded-2xl gap-8 shrink-0`}
    >
      {/* Top section with trip info */}
      {variant === 'detail' ? (
        <div className="relative flex items-center justify-between pt-4 px-4">
          <div className="flex items-center gap-1 text-white text-base font-semibold leading-6">
            <span>{startDate ? dayjs(startDate).format('D MMM') : 'N/A'}</span>
            <ArrowRight className="size-3" />
            <span>{endDate ? dayjs(endDate).format('D MMM') : 'N/A'}</span>
          </div>
          {badge}
        </div>
      ) : (
        <div className="relative flex flex-col gap-3 pt-4 px-4">
          {/* Trip name + status */}
          <div className="relative flex items-center justify-between">
            <p className="text-white text-xl font-semibold leading-[1.2] tracking-[-0.4px]">
              {tripName}
            </p>
            {badge}
          </div>

          {/* Aircraft + date range */}
          <div className="relative flex items-center gap-1 text-white/60 text-base leading-6">
            {aircraftRegistrations.length > 0 && (
              <>
                <span className="font-semibold tracking-[-0.5px]">
                  {aircraftRegistrations.join(', ')}
                </span>
                <span className="font-semibold tracking-[-0.5px]">•</span>
              </>
            )}
            <span>{startDate ? dayjs(startDate).format('D MMM') : 'N/A'}</span>
            <ArrowRight className="size-3" />
            <span>{endDate ? dayjs(endDate).format('D MMM') : 'N/A'}</span>
          </div>
        </div>
      )}

      {/* Bottom stats row */}
      <div className="relative flex items-center justify-between px-4 pt-3 pb-4">
        <StatItem icon={<Plane className="size-5 text-content-primary-white" />} label="Flights" value={flightsCount} />
        <StatItem icon={<TowerControl className="size-5 text-content-primary-white" />} label="Airports" value={airportsCount} />
        <StatItem icon={<IdCardLanyard className="size-5 text-content-primary-white" />} label="Crew" value={crewCount} />
        <StatItem icon={<Users className="size-5 text-content-primary-white" />} label="PAX" value={paxCount} />
      </div>
    </div>
  )
}

function StatItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-white/60 tracking-[0.18px]">
        {label}
      </p>
      <div className="flex items-center gap-2 text-content-primary-bold">
        {icon}
        <p className="text-sm text-content-primary-white tracking-[0.07px]">
          {value}
        </p>
      </div>
    </div>
  )
}
